import Cookies from "js-cookie";
import { env } from "./env";
var CryptoJs = require("crypto-js");

// Function to encrypt data using AES encryption
export const encrypt = (key, data) => {
  return CryptoJs.AES.encrypt(JSON.stringify(data), key).toString();
};

// Function to decrypt data using AES decryption
export const decrypt = (key, data) => {
  var decryptData = CryptoJs.AES.decrypt(data, key);
  return JSON.parse(decryptData.toString(CryptoJs.enc.Utf8));
};

// Cookie scope. Empty (the default, and every non-production environment) keeps the
// old host-only behaviour. Production sets '.buildvizer.com' so one session is shared
// across app./interior./arch. — without it, following a cross-domain persona link means
// signing in again, which makes the feature not worth using.
//
// This widens the blast radius of an existing exposure rather than creating a new one:
// the token is already JS-readable (axios has to read it to build the auth header) and
// the AES wrapper is obfuscation, not encryption — the key is a constant compiled into
// the bundle. The genuinely NEW risk is subdomain takeover: a dangling DNS record on any
// *.buildvizer.com host would now reach every persona's session. Audit the zone before
// enabling, and keep auditing it.
const cookieDomain = () => env("REACT_APP_COOKIE_DOMAIN");

// What actually needs to survive in the cookie.
//
// The cookie is only the COLD-LOAD fallback: within a tab, UserContext keeps the full
// object in sessionStorage and reads that first. Storing the whole userInfo here is a
// live hazard — it includes loginIdentifierDetails, permissions and the persona/link
// list, AES+base64 inflates it about 1.4x, browsers cap a cookie at ~4KB, and
// `Cookies.set` fails SILENTLY past that. The user simply appears logged out. The
// persona list only grows, so this would have started biting real accounts.
const COOKIE_USER_FIELDS = [
  "loginId",
  "persona",
  "accessToken",
  "activeLinkId",
  "name",
  "email",
  "username",
];

/** Reduce a userInfo object to the fields a cold page load actually needs. */
export const slimUserInfo = (data) => {
  if (!data || typeof data !== "object") return data;
  const slim = {};
  COOKIE_USER_FIELDS.forEach((field) => {
    if (data[field] !== undefined) slim[field] = data[field];
  });
  return slim;
};

// Function to set a cookie with encrypted data
export const setCookie = (key, data, options = {}) => {
  const payload = key === "_USER_AUTH_" ? slimUserInfo(data) : data;
  const encryptData = encrypt(key + "ADMIN@123!", payload);
  const domain = cookieDomain();

  // Clear any HOST-ONLY cookie of the same name before writing a domain-scoped
  // one. They are separate storage entries, so without this a browser that signed
  // in before the domain widened ends up holding BOTH — and which one document.cookie
  // yields first is not defined. The failure is a stale token silently shadowing the
  // fresh one: the user appears logged in as whoever they were last time.
  if (domain) Cookies.remove(key, { path: "/" });
  const defaultOptions = {
    secure: window.location.protocol === 'https:', 
    expires: 1,
    path: "/",
    sameSite: 'Lax',
    ...(domain ? { domain } : {}),
  };
  Cookies.set(key, encryptData, { ...defaultOptions, ...options });
};

// Function to get and decrypt a cookie
export const getCookie = (key) => {
 
  const cookieData = Cookies.get(key);
  return cookieData ? decrypt(key + "ADMIN@123!", cookieData) : null;
};

// Function to delete a Cookie
export const deleteCookie = (key) => {
  // Two removals, both required.
  //
  // A host-only cookie and a Domain= cookie with the same NAME are separate storage
  // entries, so clearing one leaves the other readable and the next getCookie() still
  // returns a session — i.e. logout silently does nothing. The first call clears
  // cookies written before the domain widened (and in local dev); the second clears
  // the shared-domain one.
  //
  // The previous implementation passed `domain: window.location.hostname`, which
  // matches NEITHER a host-only cookie nor a '.buildvizer.com' one.
  Cookies.remove(key, { path: "/" });
  const domain = cookieDomain();
  if (domain) Cookies.remove(key, { path: "/", domain });
};

// Function to get the authenticated user detail
export const getAuthToken = () => {
  return getCookie("_USER_AUTH_");
};

// Transit encryption helpers for sensitive API fields.
// Accept both FE and BE-style names to avoid runtime key mismatch issues.
const getTransitKey = () => {
  const key =
    (typeof window !== "undefined" && window.env?.REACT_APP_TRANSIT_ENC_KEY) ||
    (typeof window !== "undefined" && window.env?.TRANSIT_ENC_KEY) ||
    process.env.REACT_APP_TRANSIT_ENC_KEY ||
    process.env.TRANSIT_ENC_KEY ||
    "";
  return String(key).trim();
};

/**
 * Whether a transit key is configured at all.
 *
 * The payload-encryption interceptors use this to decide whether to tell the server "I can
 * decrypt". Advertising that without a key would have the server encrypt PII this bundle then
 * renders as ciphertext — worse than not doing it at all.
 */
export const hasTransitKey = () => Boolean(getTransitKey());

export const encryptForTransit = (data) => {
  const key = getTransitKey();
  if (!key || !data) return data;
  return CryptoJs.AES.encrypt(JSON.stringify(data), key).toString();
};

export const decryptFromTransit = (data) => {
  const key = getTransitKey();
  if (!key || !data) return data;
  try {
    var decryptData = CryptoJs.AES.decrypt(data, key);
    return JSON.parse(decryptData.toString(CryptoJs.enc.Utf8));
  } catch {
    return data;
  }
};

/** Normalize base64 (trim, strip inner whitespace, restore = padding) for OpenSSL/CryptoJS ciphertext. */
export const normalizeTransitCiphertextBase64 = (value) => {
  if (value == null || typeof value !== "string") return "";
  const t = value.trim().replace(/\s/g, "");
  if (!t) return "";
  const pad = (4 - (t.length % 4)) % 4;
  return t + "=".repeat(pad);
};

/** True if base64 decodes to OpenSSL "Salted__" + salt + ciphertext (same shape as encryptForTransit). */
export const isOpenSslSaltedCiphertextBase64 = (value) => {
  try {
    const b64 = normalizeTransitCiphertextBase64(value);
    if (!b64) return false;
    const bin = atob(b64.replace(/-/g, "+").replace(/_/g, "/"));
    return bin.length >= 8 && bin.slice(0, 8) === "Salted__";
  } catch {
    return false;
  }
};

/** Normalize phase fields from API (snake_case or camelCase) for UI lists and navigation. */
export function normalizeProjectPhasePayload(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const id = raw.projectPhaseId ?? raw.project_phase_id;
  return {
    ...raw,
    projectPhaseId: id != null ? id : raw.projectPhaseId,
    phaseStatus: raw.phaseStatus ?? raw.phase_status,
    reraNo: raw.reraNo ?? raw.rera_no,
    reraStartDate: raw.reraStartDate ?? raw.rera_start_date,
    reraEndDate: raw.reraEndDate ?? raw.rera_end_date,
  };
}

/**
 * Merge phases from GET with any optimistic rows not yet present on the server
 * (avoids wiping a newly created phase when a stale refetch completes).
 */
export function mergeProjectPhasesWithServer(serverPhases, optimisticPhases) {
  const map = new Map();
  const put = (p) => {
    const n = normalizeProjectPhasePayload(p);
    const id = n?.projectPhaseId ?? n?.project_phase_id;
    if (id == null) return;
    map.set(String(id), n);
  };
  (serverPhases || []).forEach(put);
  (optimisticPhases || []).forEach((p) => {
    const n = normalizeProjectPhasePayload(p);
    const id = n?.projectPhaseId ?? n?.project_phase_id;
    if (id == null) return;
    if (!map.has(String(id))) put(p);
  });
  return Array.from(map.values());
}